import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Check, Clipboard, Clock3, Copy, Download, ExternalLink, File, FileText, FolderOpen, KeyRound, Link2, LoaderCircle,
  RefreshCw, Search, Share2, ShieldCheck, Trash2, UploadCloud, X
} from "lucide-react";
import { api } from "../lib/api";
import { deriveShareKey, encryptBytes, fromBase64, randomBytes, toBase64 } from "../lib/crypto-core";
import type { FileRecord, FileShare } from "../types";

const shareChunkSize = 4 * 1024 * 1024;
const customLinkPattern = /^(?=.*[A-Za-z0-9])[A-Za-z0-9_-]{3,64}$/;
const textFileExtensions = /\.(txt|text|md|markdown|mdx|csv|tsv|json|xml|yaml|yml|log|ini|conf|env|js|jsx|ts|tsx|css|scss|html|htm|svg|py|rb|go|rs|java|c|h|cpp|sh|bash|zsh|sql)$/i;
const textMimePattern = /^(text\/|application\/(json|javascript|xml|yaml|x-yaml|toml|sql))/i;
type Progress = { name: string; value: number; phase: string };

function isTextFile(file: FileRecord): boolean {
  return textMimePattern.test(file.mime) || textFileExtensions.test(file.title);
}

function normalizedShareFile(file: File): File {
  if (textMimePattern.test(file.type)) return file;
  const type = /\.(md|markdown|mdx)$/i.test(file.name) ? "text/markdown"
    : /\.(txt|text|log)$/i.test(file.name) ? "text/plain" : file.type;
  return type === file.type ? file : new globalThis.File([file], file.name, { type });
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`;
}

function expiryDate(value: string): string | null {
  if (value === "never") return null;
  const expires = new Date(Date.now() + Number(value) * 86_400_000);
  return expires.toISOString();
}

export function FileSharingApp({ files, onUpload, onLoad }: {
  files: FileRecord[];
  onUpload: (file: File, onProgress: (value: number, phase?: string) => void) => Promise<FileRecord>;
  onLoad: (record: FileRecord, onProgress: (value: number, phase?: string) => void) => Promise<Blob>;
}) {
  const [shares, setShares] = useState<FileShare[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expiry, setExpiry] = useState("7");
  const [linkMode, setLinkMode] = useState<"random" | "custom">("random");
  const [customLink, setCustomLink] = useState("");
  const [password, setPassword] = useState("");
  const [encryptShare, setEncryptShare] = useState(false);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const [pickerFilter, setPickerFilter] = useState<"all" | "text">("all");
  const [pastedText, setPastedText] = useState("");
  const [pasteName, setPasteName] = useState("pasted-text.txt");
  const [copied, setCopied] = useState("");
  const [dragging, setDragging] = useState(false);
  const [busyId, setBusyId] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const refresh = useCallback(async () => {
    try {
      const response = await api.shares();
      setShares(response.shares);
      setError("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not load shared files");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  const publish = useCallback(async (fileName: string, mime: string, blob: Blob, startAt = 0) => {
    if (!blob.size) throw new Error("Empty files cannot be shared");
    const trimmedLink = customLink.trim();
    if (linkMode === "custom") {
      if (!customLinkPattern.test(trimmedLink)) {
        throw new Error("Custom links need 3–64 letters, numbers, hyphens, or underscores");
      }
    }
    if (password && password.length < 4) throw new Error("Share passwords need at least 4 characters");
    if (encryptShare && password.length < 4) throw new Error("Encrypted shares require a password of at least 4 characters");
    let uploadBlob = blob;
    let encryptionSalt: string | undefined;
    let encryptionIv: string | undefined;
    let originalByteSize: number | undefined;
    if (encryptShare) {
      setProgress({ name: fileName, value: startAt, phase: "Encrypting shared copy" });
      const salt = randomBytes(16);
      const key = await deriveShareKey(password, salt);
      const envelope = await encryptBytes(key, new Uint8Array(await blob.arrayBuffer()), "fileshare:v1");
      const ciphertext = fromBase64(envelope.ciphertext);
      uploadBlob = new Blob([ciphertext.buffer as ArrayBuffer], { type: "application/octet-stream" });
      encryptionSalt = toBase64(salt);
      encryptionIv = envelope.iv;
      originalByteSize = blob.size;
    }
    const count = Math.ceil(uploadBlob.size / shareChunkSize);
    setProgress({ name: fileName, value: startAt, phase: "Creating secure link" });
    const created = await api.initShare({
      fileName,
      mime: mime || "application/octet-stream",
      byteSize: uploadBlob.size,
      chunkSize: shareChunkSize,
      chunkCount: count,
      expiresAt: expiryDate(expiry),
      ...(linkMode === "custom" ? { customToken: trimmedLink } : {}),
      ...(password ? { password } : {}),
      ...(encryptShare ? { encrypted: true, encryptionSalt, encryptionIv, originalByteSize } : {})
    });
    try {
      for (let index = 0; index < count; index++) {
        const bytes = new Uint8Array(await uploadBlob.slice(index * shareChunkSize, Math.min(uploadBlob.size, (index + 1) * shareChunkSize)).arrayBuffer());
        const data = toBase64(bytes);
        await api.putShareChunk(created.id, index, data, (loaded, total) => {
          const ratio = total > 0 ? Math.min(1, loaded / total) : 0;
          const value = startAt + ((index + ratio) / count) * (98 - startAt);
          setProgress({ name: fileName, value: Math.round(value), phase: "Publishing shared copy" });
        });
      }
      setProgress({ name: fileName, value: 99, phase: "Finalizing link" });
      const completed = await api.completeShare(created.id);
      setShares((current) => [completed, ...current.filter((share) => share.id !== completed.id)]);
      setProgress({ name: fileName, value: 100, phase: "Ready to share" });
      setCustomLink("");
      setPassword("");
      setEncryptShare(false);
      setLinkMode("random");
      window.setTimeout(() => setProgress(null), 800);
      return completed;
    } catch (cause) {
      await api.deleteShare(created.id).catch(() => undefined);
      throw cause;
    }
  }, [customLink, encryptShare, expiry, linkMode, password]);

  const uploadAndShare = useCallback(async (selected: File[]) => {
    if (progress || !selected.length) return;
    setError("");
    for (const file of selected) {
      try {
        const shareFile = normalizedShareFile(file);
        setProgress({ name: file.name, value: 0, phase: "Saving to encrypted workspace" });
        const record = await onUpload(shareFile, (value, phase) => setProgress({
          name: file.name,
          value: Math.round(value * .48),
          phase: phase || "Saving to encrypted workspace"
        }));
        await publish(record.title, record.mime || shareFile.type || "application/octet-stream", shareFile, 50);
      } catch (cause) {
        setProgress(null);
        setError(cause instanceof Error ? cause.message : `Could not share ${file.name}`);
        break;
      }
    }
  }, [onUpload, progress, publish]);

  const shareExisting = useCallback(async (record: FileRecord) => {
    if (progress) return;
    setPickerOpen(false);
    setError("");
    try {
      setProgress({ name: record.title, value: 0, phase: "Opening encrypted workspace file" });
      const blob = await onLoad(record, (value, phase) => setProgress({
        name: record.title,
        value: Math.round(value * .45),
        phase: phase || "Decrypting workspace file"
      }));
      await publish(record.title, record.mime, blob, 48);
    } catch (cause) {
      setProgress(null);
      setError(cause instanceof Error ? cause.message : `Could not share ${record.title}`);
    }
  }, [onLoad, progress, publish]);

  const sharePastedText = useCallback(async () => {
    if (progress || !pastedText.length) return;
    const name = pasteName.trim() || "pasted-text.txt";
    const file = new globalThis.File([pastedText], name, { type: "text/plain" });
    await uploadAndShare([file]);
    setPastedText("");
  }, [pasteName, pastedText, progress, uploadAndShare]);

  const copyLink = async (share: FileShare) => {
    try {
      await navigator.clipboard.writeText(share.url);
      setCopied(share.id);
      window.setTimeout(() => setCopied(""), 1600);
    } catch {
      window.prompt("Copy this link", share.url);
    }
  };

  const rotateLink = async (share: FileShare) => {
    if (!window.confirm(`Rotate the public link for “${share.fileName}”? The old URL will stop working immediately.`)) return;
    setBusyId(share.id);
    setError("");
    try {
      const updated = await api.rotateShare(share.id);
      setShares((current) => current.map((item) => item.id === updated.id ? updated : item));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not rotate link");
    } finally {
      setBusyId("");
    }
  };

  const setSharePassword = async (share: FileShare) => {
    const next = window.prompt(
      share.hasPassword
        ? `New password for “${share.fileName}” (leave blank to remove protection)`
        : `Password for “${share.fileName}”`
    );
    if (next === null) return;
    const trimmed = next.trim();
    if (trimmed && trimmed.length < 4) {
      setError("Share passwords need at least 4 characters");
      return;
    }
    setBusyId(share.id);
    setError("");
    try {
      const updated = await api.updateShare(share.id, { password: trimmed ? trimmed : null });
      setShares((current) => current.map((item) => item.id === updated.id ? updated : item));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not update password");
    } finally {
      setBusyId("");
    }
  };

  const setCustomShareLink = async (share: FileShare) => {
    const next = window.prompt(`Custom link for “${share.fileName}”`, share.token);
    if (next === null) return;
    const trimmed = next.trim();
    if (!customLinkPattern.test(trimmed)) {
      setError("Custom links need 3–64 letters, numbers, hyphens, or underscores");
      return;
    }
    setBusyId(share.id);
    setError("");
    try {
      const updated = await api.updateShare(share.id, { customToken: trimmed });
      setShares((current) => current.map((item) => item.id === updated.id ? updated : item));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not update link");
    } finally {
      setBusyId("");
    }
  };

  const revoke = async (share: FileShare) => {
    if (!window.confirm(`Revoke the public link for “${share.fileName}”? The workspace file will not be deleted.`)) return;
    try {
      await api.deleteShare(share.id);
      setShares((current) => current.filter((item) => item.id !== share.id));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not revoke link");
    }
  };

  const pickerFiles = useMemo(() => {
    const query = pickerQuery.trim().toLocaleLowerCase();
    return files.filter((file) => !file.deletedAt && (pickerFilter === "all" || isTextFile(file)) && (!query || `${file.title} ${file.mime}`.toLocaleLowerCase().includes(query)))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }, [files, pickerFilter, pickerQuery]);
  const activeShares = shares.filter((share) => !share.expiresAt || new Date(share.expiresAt) > new Date()).length;
  const shareOptionsDisabled = Boolean(progress);

  return <div className="fileshare-app">
    <header className="fileshare-header">
      <div className="fileshare-brand"><span><Share2 size={21} /></span><div><strong>File Sharing</strong><small>{activeShares} active {activeShares === 1 ? "link" : "links"} · files.nkriz.ir</small></div></div>
      <label className="fileshare-expiry"><Clock3 size={15} /><span>Link expires</span><select value={expiry} onChange={(event) => setExpiry(event.target.value)} disabled={shareOptionsDisabled}><option value="1">After 1 day</option><option value="7">After 7 days</option><option value="30">After 30 days</option><option value="never">Never</option></select></label>
    </header>

    <main className="fileshare-body">
      <section
        className={`fileshare-drop ${dragging ? "dragging" : ""}`}
        onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => { if (event.currentTarget === event.target) setDragging(false); }}
        onDrop={(event) => { event.preventDefault(); setDragging(false); void uploadAndShare(Array.from(event.dataTransfer.files)); }}
      >
        <span className="fileshare-drop-icon"><UploadCloud size={30} /></span>
        <div><h2>Share a file in seconds</h2><p>Drop files here or choose a source. New uploads are also saved in your encrypted workspace. Shared copies are plaintext by default, with optional browser-side encryption below.</p></div>
        <div className="fileshare-drop-actions">
          <button className="fileshare-primary" onClick={() => inputRef.current?.click()} disabled={shareOptionsDisabled}><UploadCloud size={16} />Upload and share</button>
          <button onClick={() => setPickerOpen(true)} disabled={shareOptionsDisabled || files.length === 0}><FolderOpen size={16} />Choose from workspace</button>
        </div>
        <input ref={inputRef} hidden type="file" multiple onChange={(event) => { void uploadAndShare(Array.from(event.target.files ?? [])); event.target.value = ""; }} />
      </section>

      <section className="fileshare-text-composer">
        <div className="fileshare-text-heading"><span><Clipboard size={17} /><strong>Paste text</strong></span><small>Save it to your encrypted workspace and create a share link.</small></div>
        <textarea value={pastedText} onChange={(event) => setPastedText(event.target.value)} disabled={shareOptionsDisabled} placeholder="Paste notes, logs, code, or any other text here…" spellCheck={false} />
        <div className="fileshare-text-actions"><label><span>File name</span><input value={pasteName} onChange={(event) => setPasteName(event.target.value)} disabled={shareOptionsDisabled} spellCheck={false} /></label><button className="fileshare-primary" onClick={() => void sharePastedText()} disabled={shareOptionsDisabled || !pastedText.length}><Clipboard size={15} />Save &amp; share text</button></div>
      </section>

      <section className="fileshare-options">
        <div className="fileshare-option">
          <span><Link2 size={15} /><strong>Public link</strong></span>
          <div className="fileshare-link-mode">
            <button type="button" className={linkMode === "random" ? "active" : ""} disabled={shareOptionsDisabled} onClick={() => setLinkMode("random")}>Random</button>
            <button type="button" className={linkMode === "custom" ? "active" : ""} disabled={shareOptionsDisabled} onClick={() => setLinkMode("custom")}>Custom</button>
          </div>
          {linkMode === "custom" ? <label className="fileshare-slug"><small>files.nkriz.ir/s/</small><input value={customLink} onChange={(event) => setCustomLink(event.target.value)} disabled={shareOptionsDisabled} placeholder="my-project-docs" spellCheck={false} autoComplete="off" /></label> : <small className="fileshare-option-hint">A long unguessable token is generated for each share.</small>}
        </div>
        <div className="fileshare-option">
          <span><KeyRound size={15} /><strong>Password</strong></span>
          <input type="password" value={password} onChange={(event) => { const value = event.target.value; setPassword(value); if (value.length < 4) setEncryptShare(false); }} disabled={shareOptionsDisabled} placeholder="Optional recipient password" autoComplete="new-password" />
          <label className="fileshare-encryption-toggle"><input type="checkbox" checked={encryptShare} onChange={(event) => setEncryptShare(event.target.checked)} disabled={shareOptionsDisabled || password.length < 4} /><span><ShieldCheck size={14} /><strong>Encrypt shared copy</strong></span><small>{password.length < 4 ? "Set a 4+ character password first" : "Ciphertext is stored on the server; recipients decrypt in their browser."}</small></label>
        </div>
      </section>

      {progress && <section className="fileshare-progress" aria-live="polite"><div><LoaderCircle className={progress.value < 100 ? "spin" : ""} size={17} /><span><strong>{progress.phase}</strong><small>{progress.name}</small></span><b>{progress.value}%</b></div><i><span style={{ width: `${progress.value}%` }} /></i></section>}
      {error && <div className="fileshare-error"><span>{error}</span><button onClick={() => setError("")} aria-label="Dismiss error"><X size={14} /></button></div>}

      <section className="fileshare-list-section">
        <div className="fileshare-list-title"><div><Link2 size={18} /><span><strong>Shared links</strong><small>Anyone with a live link can download its file{shares.some((share) => share.hasPassword) ? " (unless password-protected)" : ""}</small></span></div><button onClick={() => void refresh()} disabled={loading}>Refresh</button></div>
        <div className="fileshare-list">
          {loading ? <div className="fileshare-empty"><LoaderCircle className="spin" /><span>Loading shared links…</span></div> : shares.length === 0 ? <div className="fileshare-empty"><ShieldCheck size={30} /><strong>No shared files yet</strong><span>Create a link above without moving or exposing the original workspace file.</span></div> : shares.map((share) => {
            const expired = Boolean(share.expiresAt && new Date(share.expiresAt) <= new Date());
            const busy = busyId === share.id;
            return <article key={share.id} className={`fileshare-row ${expired ? "expired" : ""}`}>
              <span className="fileshare-file-icon"><File size={21} /></span>
              <div className="fileshare-file-copy">
                <strong>{share.fileName}</strong>
                <small>{formatBytes(share.byteSize)} · {share.mime}{share.hasPassword ? " · Password protected" : ""}{share.encrypted ? " · End-to-end encrypted" : ""}</small>
                <span><Clock3 size={11} />{expired ? "Expired" : share.expiresAt ? `Expires ${new Date(share.expiresAt).toLocaleString()}` : "Never expires"}</span>
                <code title={share.url}>/s/{share.token}</code>
              </div>
              <div className="fileshare-downloads"><Download size={14} /><strong>{share.downloads.toLocaleString()}</strong><small>downloads</small></div>
              <div className="fileshare-row-actions">
                <button onClick={() => void copyLink(share)} disabled={expired || busy} title="Copy public link">{copied === share.id ? <Check size={16} /> : <Copy size={16} />}<span>{copied === share.id ? "Copied" : "Copy link"}</span></button>
                <button onClick={() => void setCustomShareLink(share)} disabled={expired || busy} title="Choose custom link"><Link2 size={16} /></button>
                <button onClick={() => void setSharePassword(share)} disabled={expired || busy || share.encrypted} title={share.encrypted ? "Encrypted share password is fixed; create a new share to change it" : share.hasPassword ? "Change or remove password" : "Add password"}><KeyRound size={16} /></button>
                <button onClick={() => void rotateLink(share)} disabled={expired || busy} title="Rotate to a new random link"><RefreshCw size={16} className={busy ? "spin" : ""} /></button>
                <a href={share.url} target="_blank" rel="noreferrer" className={expired ? "disabled" : ""} title="Open recipient page"><ExternalLink size={16} /></a>
                <button className="danger" onClick={() => void revoke(share)} disabled={busy} title="Revoke link"><Trash2 size={16} /></button>
              </div>
            </article>;
          })}
        </div>
      </section>
    </main>

    {pickerOpen && <div className="fileshare-picker-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setPickerOpen(false); }}>
      <section className="fileshare-picker"><header><div><FolderOpen size={19} /><span><strong>Choose from workspace</strong><small>{pickerFiles.length} {pickerFilter === "text" ? "text " : ""}files available</small></span></div><button onClick={() => setPickerOpen(false)} aria-label="Close"><X size={17} /></button></header><label><Search size={15} /><input autoFocus value={pickerQuery} onChange={(event) => setPickerQuery(event.target.value)} placeholder="Search workspace files" /></label><div className="fileshare-picker-filter"><button type="button" className={pickerFilter === "all" ? "active" : ""} onClick={() => setPickerFilter("all")}>All files</button><button type="button" className={pickerFilter === "text" ? "active" : ""} onClick={() => setPickerFilter("text")}><FileText size={13} />Text files</button></div><div className="fileshare-picker-list">{pickerFiles.length ? pickerFiles.map((file) => <button key={file.id} onClick={() => void shareExisting(file)}><span>{isTextFile(file) ? <FileText size={18} /> : <File size={18} />}</span><div><strong>{file.title}</strong><small>{formatBytes(file.size)} · {file.mime}</small></div><Share2 size={16} /></button>) : <p>No matching workspace files.</p>}</div></section>
    </div>}
  </div>;
}
